import {
  COMPANION_LAYER_ROLES,
  DEFAULT_COMPANION_LAYER_DRAW_ORDER,
} from '../gisServicesTypes';
import { parseOgcServiceUrl } from './parseOgcServiceUrl';
import type {
  CompanionLayerRole,
  RasterCompanionConfig,
  RasterCompanionProtocol,
  ResolvedRasterCompanion,
} from './rasterCompanionTypes';

function normalizeLayerNames(config: RasterCompanionConfig): string[] {
  return [
    ...new Set(
      (config.layer_names || [])
        .map(name => name?.trim())
        .filter((name): name is string => Boolean(name)),
    ),
  ];
}

function normalizeProtocol(
  protocol?: RasterCompanionProtocol,
): RasterCompanionProtocol {
  return protocol === 'wmts' ? 'wmts' : 'wms';
}

/**
 * Normalize the configured draw order into a valid, complete permutation of
 * the known roles (bottom -> top). Unknown/duplicate entries are dropped and
 * any missing roles are appended in default order, so the result is always a
 * full ordering UNITE can rely on.
 */
function normalizeLayerDrawOrder(
  configured?: CompanionLayerRole[],
): CompanionLayerRole[] {
  if (!Array.isArray(configured) || configured.length === 0) {
    return [...DEFAULT_COMPANION_LAYER_DRAW_ORDER];
  }

  const seen = new Set<CompanionLayerRole>();
  const ordered: CompanionLayerRole[] = [];
  configured.forEach(role => {
    if (COMPANION_LAYER_ROLES.includes(role) && !seen.has(role)) {
      seen.add(role);
      ordered.push(role);
    }
  });

  DEFAULT_COMPANION_LAYER_DRAW_ORDER.forEach(role => {
    if (!seen.has(role)) {
      ordered.push(role);
    }
  });

  return ordered;
}

/**
 * Resolve a raster companion from Andromeda `attributes.raster_companion`.
 * Returns null when disabled / incomplete — WFS loads alone.
 */
export function resolveRasterCompanion(args: {
  rasterCompanion?: RasterCompanionConfig | null;
  /** Auth (and other) params from the WFS URL; merged with raster URL params. */
  wfsCustomParameters?: Record<string, string>;
}): ResolvedRasterCompanion | null {
  const config = args.rasterCompanion;
  if (!config?.enabled) {
    return null;
  }

  const rawUrl = config.service_url?.trim();
  const layerNames = normalizeLayerNames(config);
  if (!rawUrl || layerNames.length === 0) {
    return null;
  }

  const protocol = normalizeProtocol(config.protocol);
  const parsed = parseOgcServiceUrl(rawUrl);
  const customParameters = {
    ...args.wfsCustomParameters,
    ...parsed.customParameters,
  };

  const secondaryTitle = config.secondary_cql?.title?.trim();
  const secondaryFilter = config.secondary_cql?.cql_filter?.trim();
  const secondaryCql =
    secondaryTitle || secondaryFilter
      ? {
          ...(secondaryTitle ? { title: secondaryTitle } : {}),
          ...(secondaryFilter ? { cql_filter: secondaryFilter } : {}),
        }
      : undefined;

  const defaultStackingOrder = Number.isInteger(config.default_stacking_order)
    ? config.default_stacking_order
    : undefined;

  const layerDrawOrder = normalizeLayerDrawOrder(config.layer_draw_order);

  return {
    protocol,
    label: layerNames[0],
    serviceUrl: parsed.url,
    layerNames,
    layerDrawOrder,
    ...(protocol === 'wms' && config.custom_layer_parameters
      ? { customLayerParameters: config.custom_layer_parameters }
      : {}),
    ...(Object.keys(customParameters).length > 0 ? { customParameters } : {}),
    ...(defaultStackingOrder !== undefined ? { defaultStackingOrder } : {}),
    ...(secondaryCql ? { secondaryCql } : {}),
  };
}
